import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  PopperProps,
  SxProps,
  TextField,
  Typography,
} from '@mui/material';
import { CSSProperties, HTMLAttributes, useCallback, useMemo, useState } from 'react';
import omit from 'lodash-es/omit';
import { ICompanySearchResponseDataModel } from '../../data-models/company-search-result.data-model';
import { getRealCompanyId, searchCompanies, SearchType } from '../../services/queries/MaggieCompanyQueries';
import { IOptionsFromSearchConfig, useAsyncAutoComplete } from '../Form/Display/useAsyncAutomplete';
import { CompanyOption } from '../Form/Display/FieldCompanySearch';
import { CompanySearchResponseWithRealId } from '../../schemas/CompanySearchResponse.schema';
import { MUIAvatar } from '../Avatar/MUIAvatar';

export const DummyCreateId = 'dummyCreate';

export const SearchThrottleDelayMs = 200;

export interface ICompanySearchProps {
  autoFocus?: boolean;
  disabled?: boolean;
  disablePortal?: boolean;
  /** Size the results menu to the input instead of the shared minimum width. */
  fitMenuToInput?: boolean;
  fullWidth?: boolean;
  initialValue?: ICompanySearchResponseDataModel | undefined;
  onChange: (company: CompanySearchResponseWithRealId) => void | Promise<void>;
  /** Fired when the selection is cleared (the "×"). `onChange` only fires on a
   * pick, so consumers that must react to clearing (e.g. resetting dependent
   * fields) need this. */
  onClear?: () => void;
  /** Empty-field prompt; defaults to the full "name or website" hint. */
  placeholder?: string;
  onCompanyCreateClick?: (typedCompanyName: string) => void;
  showCreateOption?: boolean;
  /** Show the picked company's logo at the start of the input; pairs with `showSelectedOption`. */
  showSelectedLogo?: boolean;
  showSelectedOption?: boolean;
  style?: CSSProperties;
  sx?: SxProps;
  showPortcoOnly?: boolean;
}

export function CompanySearch({
  autoFocus,
  disabled = false,
  disablePortal = true,
  fitMenuToInput = false,
  fullWidth,
  initialValue,
  onChange,
  onClear,
  placeholder,
  onCompanyCreateClick,
  showCreateOption = false,
  showSelectedLogo = false,
  showSelectedOption,
  style,
  sx,
  showPortcoOnly = false,
}: ICompanySearchProps) {
  const [isGettingCompanyId, setIsGettingCompanyId] = useState(false);
  const searchType = showPortcoOnly ? SearchType.Portfolio : SearchType.Company;
  const autoCompleteConfig = useMemo(() => {
    const conf: IOptionsFromSearchConfig<ICompanySearchResponseDataModel, false> = {
      generateCreateOption: showCreateOption
        ? (currentText) => {
            return {
              id: DummyCreateId,
              fields: {
                website: '',
                logo: '',
                description: `Select to create a new company`,
                name: `Create new company with name '${currentText}'`,
                type: SearchType.Portfolio,
              },
            };
          }
        : undefined,
      initialValue: initialValue ?? null,
      // Maggie-index search only. Deliberately NOT the sourcing fan-out
      // (searchCompaniesFromAllSources): external sourcing picks carry provider-specific
      // ids that the getRealCompanyId resolution below would turn into NaN.
      searchFn: (searchTerm) => searchCompanies(searchTerm, searchType, false),
      onChange: async (newValue: ICompanySearchResponseDataModel | null) => {
        if (newValue === null) {
          onClear?.();
          return;
        }

        setIsGettingCompanyId(true);
        try {
          // Resolve every pick to its real Maggie company id. getRealCompanyId is a
          // passthrough for numeric ids and resolves vendor-<crunchbaseId> picks; the old
          // type gate wrongly stamped Maggie 'discovery'/'company' hits with -1.
          const realId = (await getRealCompanyId(newValue.id)).id;
          await onChange({ ...newValue, id: realId });
        } finally {
          setIsGettingCompanyId(false);
        }
      },
      onCreateClick: onCompanyCreateClick,
    };

    return conf;
  }, [initialValue, onChange, onClear, onCompanyCreateClick, searchType, showCreateOption]);
  const { isSearching, onClose, onInputChange, onValueChange, open, options, value } =
    useAsyncAutoComplete<ICompanySearchResponseDataModel>(autoCompleteConfig);

  const getOptionLabel = useCallback(
    (e: ICompanySearchResponseDataModel) => {
      if (!showSelectedOption) {
        return '';
      }
      return e.fields.name;
    },
    [showSelectedOption]
  );

  const autocompleteProps = {
    componentsProps: {
      popper: {
        placement: 'bottom-start',
        // MUI sizes the menu to the input; the minimum keeps logo + name + website
        // readable where the input is narrow (form fields, table cells).
        sx: fitMenuToInput ? undefined : { minWidth: '25rem' },
      } as PopperProps,
    },
  };

  const selectedLogo =
    showSelectedLogo && value ? (
      <InputAdornment position='start'>
        <MUIAvatar
          size='small'
          src={value.fields.logo}
          nameParts={value.fields.name.split(' ')}
          style={{ marginRight: '-0.5rem' }}
        />
      </InputAdornment>
    ) : undefined;

  return (
    <Autocomplete
      autoHighlight
      blurOnSelect
      style={style}
      disablePortal={disablePortal}
      disabled={disabled || isGettingCompanyId}
      fullWidth={fullWidth}
      filterOptions={(options) => options}
      getOptionLabel={getOptionLabel}
      id='companies-autocomplete'
      isOptionEqualToValue={(option, newValue) => {
        return option.id === newValue.id;
      }}
      noOptionsText={null}
      onChange={onValueChange}
      onClose={onClose}
      onInputChange={onInputChange}
      open={open}
      options={options}
      popupIcon={null}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            autoFocus={autoFocus ?? false}
            placeholder={
              isGettingCompanyId
                ? 'Fetching company details...'
                : (placeholder ?? 'Search for a company name or website')
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: selectedLogo ?? params.InputProps.startAdornment,
              endAdornment: (
                <>
                  {isSearching || isGettingCompanyId ? (
                    <CircularProgress color='secondary' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        );
      }}
      renderOption={(props: HTMLAttributes<HTMLLIElement>, option: ICompanySearchResponseDataModel) => {
        const otherProps = omit(props, 'key');
        const addOptionClasses = props.className ? `${props.className} add-option` : 'add-option';
        if (option.id === DummyCreateId) {
          return (
            <Box {...otherProps} key={option.id} component='li' className={addOptionClasses}>
              <Typography variant={'caption'}>{option.fields.name}</Typography>
            </Box>
          );
        }
        return <CompanyOption company={option} key={option.id} otherProps={otherProps} />;
      }}
      sx={sx}
      value={value}
      {...autocompleteProps}
    />
  );
}
